import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Edit2, Trash2, Loader2, Save, X,
  CreditCard, Users, TrendingUp, DollarSign, Check
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMemberships = () => {
  const { toast } = useToast();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingMembership, setEditingMembership] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    credits: '',
    benefits: '',
    popular: false,
    active: true,
    description: ''
  });

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/memberships?practiceId=demo-practice-001`);
      const data = await response.json();
      setMemberships(data);
    } catch (err) {
      console.error('Error fetching memberships:', err);
      toast({
        title: 'Error',
        description: 'Failed to load memberships.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const membershipData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        credits: parseInt(formData.credits) || 0,
        benefits: formData.benefits.split('\n').map(b => b.trim()).filter(Boolean),
        practiceId: 'demo-practice-001'
      };

      let response;
      if (editingMembership) {
        response = await fetch(`${API}/memberships/${editingMembership.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(membershipData)
        });
      } else {
        response = await fetch(`${API}/memberships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(membershipData)
        });
      }

      if (!response.ok) throw new Error('Failed to save membership');

      toast({
        title: editingMembership ? 'Membership Updated' : 'Membership Added',
        description: `${formData.name} has been ${editingMembership ? 'updated' : 'added'} successfully.`
      });

      resetForm();
      fetchMemberships();
    } catch (err) {
      console.error('Error saving membership:', err);
      toast({
        title: 'Error',
        description: 'Failed to save membership. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (membership) => {
    setEditingMembership(membership);
    setFormData({
      name: membership.name || '',
      price: membership.price?.toString() || '',
      credits: membership.credits?.toString() || '',
      benefits: (membership.benefits || []).join('\n'),
      popular: membership.popular || false,
      active: membership.active !== false,
      description: membership.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (membership) => {
    if (!window.confirm(`Are you sure you want to delete "${membership.name}"?`)) return;

    try {
      const response = await fetch(`${API}/memberships/${membership.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete membership');

      toast({
        title: 'Membership Deleted',
        description: `${membership.name} has been deleted.`
      });

      fetchMemberships();
    } catch (err) {
      console.error('Error deleting membership:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete membership.',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      credits: '',
      benefits: '',
      popular: false,
      active: true,
      description: ''
    });
    setEditingMembership(null);
    setShowForm(false);
  };

  // Mock stats - in production, these would come from actual data
  const totalMembers = 146;
  const totalRevenue = memberships.reduce((sum, m) => sum + (m.price || 0) * 10, 0); // Mock calculation

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading memberships...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Membership Plans</h1>
          <p className="text-gray-600 mt-2">Create and manage membership tiers for your patients</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Membership
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Plans</p>
                <p className="text-2xl font-bold">{memberships.length}</p>
              </div>
              <CreditCard className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Members</p>
                <p className="text-2xl font-bold">{totalMembers}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Monthly Revenue</p>
                <p className="text-2xl font-bold">${totalRevenue.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg. Price</p>
                <p className="text-2xl font-bold">
                  ${memberships.length > 0 ? Math.round(memberships.reduce((sum, m) => sum + (m.price || 0), 0) / memberships.length) : 0}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Membership Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingMembership ? 'Edit Membership' : 'Add New Membership'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Create a membership plan with credits and benefits
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Plan Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Gold Membership"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Monthly Price *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="price"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="99"
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="credits">Monthly Credits</Label>
                    <Input
                      id="credits"
                      type="number"
                      value={formData.credits}
                      onChange={(e) => setFormData({...formData, credits: e.target.value})}
                      placeholder="100"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of this membership tier"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="benefits">Benefits (one per line)</Label>
                  <Textarea
                    id="benefits"
                    value={formData.benefits}
                    onChange={(e) => setFormData({...formData, benefits: e.target.value})}
                    placeholder="Priority booking&#10;10% off all treatments&#10;Free monthly facial&#10;Credit rollover"
                    rows={5}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.popular}
                      onChange={(e) => setFormData({...formData, popular: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Mark as Popular/Recommended</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingMembership ? 'Update Plan' : 'Add Plan'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Memberships List */}
      {memberships.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No membership plans</h3>
            <p className="text-gray-500 mb-4">Create your first membership plan to get started</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Membership
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {memberships.map((membership) => (
            <Card key={membership.id} className={`relative ${membership.popular ? 'ring-2 ring-primary' : ''}`}>
              {membership.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary">Most Popular</Badge>
                </div>
              )}
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {membership.name}
                      {!membership.active && (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>{membership.description}</CardDescription>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(membership)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(membership)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <span className="text-3xl font-bold">${membership.price}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                
                {membership.credits > 0 && (
                  <p className="text-sm text-gray-600 mb-4">
                    {membership.credits} credits included monthly
                  </p>
                )}

                {membership.benefits && membership.benefits.length > 0 && (
                  <ul className="space-y-2">
                    {membership.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMemberships;
